import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import { formatDate } from '../../../utils/formatters';
import { AllStoresChip, CollaboratorStatusChip, accessSummary, capabilities, roleSummary } from './collaboratorStatus';
import { displayStatus } from './collaboratorFilters';

// With `getRowHeight="auto"` (two-line name cell) DataGrid stops vertically
// centering the other cells; this restores it (same as Roles).
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

// Inline "Enviar convite" for collaborators whose invitation was never sent,
// and "Reenviar convite" when the link expired (the kebab has both, plus
// "Reenviar" for invites still valid).
export const showsSendInviteButton = (collaborator) =>
  ['pending', 'expired'].includes(displayStatus(collaborator)) && Boolean(capabilities(collaborator).can_send_invite);

export function SendInviteButton({ t, collaborator, onSendInvite, sending }) {
  return (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      startIcon={<SendOutlinedIcon />}
      disabled={sending}
      onClick={() => onSendInvite(collaborator)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {displayStatus(collaborator) === 'expired' ? t('collaborators.invite.resend') : t('collaborators.invite.send')}
    </Button>
  );
}

// Kebab actions, filtered by the record's capabilities from the API —
// shared by the grid and the mobile list.
export const buildRowActions = ({ t, collaborator, onView, onEdit, onSendInvite, onDelete, onRestore }) => {
  const can = capabilities(collaborator);
  return [
    { label: t('collaborators.actions.view'), icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => onView(collaborator) },
    ...(can.can_edit
      ? [{ label: t('collaborators.actions.edit'), icon: <EditOutlinedIcon fontSize="small" />, onClick: () => onEdit(collaborator) }]
      : []),
    ...(can.can_send_invite
      ? [
          {
            label: collaborator.status === 'invited' ? t('collaborators.invite.resend') : t('collaborators.invite.send'),
            icon: <SendOutlinedIcon fontSize="small" />,
            onClick: () => onSendInvite(collaborator),
          },
        ]
      : []),
    ...(can.can_restore
      ? [{ label: t('collaborators.actions.restore'), icon: <RestoreOutlinedIcon fontSize="small" />, onClick: () => onRestore(collaborator) }]
      : []),
    ...(can.can_delete
      ? [
          {
            label: t('collaborators.actions.delete'),
            icon: <DeleteOutlineIcon fontSize="small" />,
            color: 'error',
            onClick: () => onDelete(collaborator),
          },
        ]
      : []),
  ];
};

export const buildCollaboratorColumns = ({ t, language, sendingInviteId, ...handlers }) => [
  {
    field: 'name',
    headerName: t('collaborators.fields.name'),
    flex: 1,
    minWidth: 220,
    resizable: false,
    renderCell: (params) => (
      <Stack spacing={0} sx={{ py: 1 }}>
        <Link component={RouterLink} to={`/collaborators/${params.row.uuid}`} variant="body2" underline="hover">
          {params.row.name}
        </Link>
        <Typography variant="caption" color="text.secondary">
          {params.row.email}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'access',
    headerName: t('collaborators.fields.access'),
    width: 180,
    valueGetter: (value, row) => accessSummary(t, row),
    renderCell: (params) => (
      <CenteredCell>
        {params.row.business_scope === 'all' ? <AllStoresChip t={t} /> : params.value}
      </CenteredCell>
    ),
  },
  {
    field: 'role',
    headerName: t('collaborators.fields.role'),
    width: 160,
    valueGetter: (value, row) => roleSummary(t, row),
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  {
    field: 'department',
    headerName: t('collaborators.fields.department'),
    width: 150,
    valueGetter: (value, row) => row.department?.name ?? '',
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  {
    field: 'status',
    headerName: t('collaborators.fields.status'),
    width: 180,
    valueGetter: (value, row) => t(`collaborators.statuses.${displayStatus(row)}`),
    renderCell: (params) => (
      <CenteredCell>
        <CollaboratorStatusChip t={t} collaborator={params.row} />
      </CenteredCell>
    ),
  },
  {
    field: 'invited_at',
    headerName: t('collaborators.fields.invitedAt'),
    width: 190,
    type: 'dateTime',
    valueGetter: (value, row) => (row.invitation?.invited_at ? new Date(row.invitation.invited_at) : null),
    renderCell: (params) => (
      <CenteredCell>
        {showsSendInviteButton(params.row) ? (
          <SendInviteButton
            t={t}
            collaborator={params.row}
            onSendInvite={handlers.onSendInvite}
            sending={sendingInviteId === params.row.uuid}
          />
        ) : (
          formatDate(params.value, language)
        )}
      </CenteredCell>
    ),
  },
  {
    field: 'rowActions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    align: 'center',
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu actions={buildRowActions({ t, collaborator: params.row, ...handlers })} />
      </CenteredCell>
    ),
  },
];
